import axios from 'axios';
import config from './config';

import { getAuthHeader } from './user-actions';
import { MemoriesDataActions } from './memory-slice';

const CACHE_PREFIX = 'tribut_memories_';

const getLocalMemories = tributId => {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${tributId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      Array.isArray(parsed.items) &&
      parsed.items.length > 0
    ) {
      return parsed;
    }
  } catch (e) {
    // Si localStorage está lleno o falla JSON.parse
    console.warn('Error reading from localStorage', e);
  }
  return null;
};

const saveLocalMemories = (tributId, items) => {
  try {
    localStorage.setItem(
      `${CACHE_PREFIX}${tributId}`,
      JSON.stringify({
        timestamp: Date.now(),
        items
      })
    );
  } catch (e) {
    console.warn(
      'Error saving to localStorage (quota exceeded?)',
      e
    );
  }
};

export const getMemoryList = (tributId, forceRefresh = false) => {
  return async (dispatch, getState) => {
    const state = getState();
    const currentMemoryCache =
      state.memoriesData?.byTributId?.[tributId];

    // 1. Mostrar inmediatamente datos en caché (Redux en RAM o localStorage en disco) en 0ms
    let hasLocalData = false;
    let initialItems = null;

    if (!forceRefresh) {
      if (
        Array.isArray(currentMemoryCache) &&
        currentMemoryCache.length > 0 &&
        currentMemoryCache !== 'error'
      ) {
        hasLocalData = true;
        initialItems = currentMemoryCache;
      } else {
        const localCached = getLocalMemories(tributId);
        if (
          localCached &&
          Array.isArray(localCached.items) &&
          localCached.items.length > 0
        ) {
          hasLocalData = true;
          initialItems = localCached.items;
        }
      }
    }

    if (hasLocalData && initialItems) {
      // Pintamos inmediatamente lo que hay guardado para que no haya parpadeo ni espera
      dispatch(
        MemoriesDataActions.info({
          items: initialItems,
          tributId
        })
      );
    } else {
      // Solo mostramos spinner de pantalla completa si no hay nada guardado
      dispatch(MemoriesDataActions.setLoading(true));
    }

    // 2. Consulta a la API en segundo plano SIEMPRE (para detectar si otro dispositivo subió fotos/videos)
    try {
      let response = await axios.get(`${config.API_MEMORY}`, {
        params: {
          tributId
        },
        headers: getAuthHeader()
      });

      if (
        response &&
        response.data &&
        response.data.httpStatus === 200
      ) {
        const memoryItems =
          Array.isArray(response.data.memorys) &&
          response.data.memorys.length > 0
            ? response.data.memorys
            : 'error';

        if (Array.isArray(memoryItems)) {
          // Guardar siempre lo más reciente en localStorage
          saveLocalMemories(tributId, memoryItems);

          // Verificar si hay cambios respecto a lo que ya mostramos
          const prevKey = Array.isArray(initialItems)
            ? initialItems
                .map(m => m.id || m.memoryId || m.name || m.link)
                .join(',')
            : '';
          const newKey = memoryItems
            .map(m => m.id || m.memoryId || m.name || m.link)
            .join(',');

          if (
            !hasLocalData ||
            prevKey !== newKey ||
            initialItems?.length !== memoryItems.length
          ) {
            dispatch(
              MemoriesDataActions.info({
                items: memoryItems,
                tributId
              })
            );
          }
        } else {
          // Si la respuesta fue vacía ('error') y no teníamos datos previos
          if (!hasLocalData) {
            dispatch(
              MemoriesDataActions.info({
                items: 'error',
                tributId
              })
            );
          }
        }
      } else if (!hasLocalData) {
        dispatch(
          MemoriesDataActions.info({
            items: 'error',
            tributId
          })
        );
      }
    } catch (err) {
      console.warn(
        '[MEMORIES] Error revalidando memorias en segundo plano:',
        err
      );
      if (!hasLocalData) {
        dispatch(
          MemoriesDataActions.info({
            items: 'error',
            tributId
          })
        );
      }
    } finally {
      dispatch(MemoriesDataActions.setLoading(false));
    }
    return null;
  };
};

// export const getMemoryList = async tributId => {
//   let response = await axios.get(`${config.API_MEMORY}`, {
//     params: {
//       tributId
//     },
//     headers: getAuthHeader()
//   });
//   if (
//     response &&
//     response.data &&
//     response.data.httpStatus === 200
//   ) {
//     return response.data.memorys;
//   }
//   return null;
// };

export const downloadFileHandler = link => {
  const typeFile = link.split('.');
  axios({
    url: link, //your url
    method: 'GET',
    responseType: 'blob' // important
  }).then(response => {
    // create file link in browser's memory
    const href = URL.createObjectURL(response.data);

    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', `memory.${typeFile[4]}`); //or any other extension
    document.body.appendChild(link);
    link.click();

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  });
};

export const getMemoryDetails = async memoryId => {
  let response = await axios.get(`${config.API_MEMORY}/details`, {
    params: {
      memoryId
    }
  });
  if (
    response &&
    response.data &&
    response.data.httpStatus === 200
  ) {
    return response.data.memory;
  }
  return null;
};

export const getMemoryComments = async memoryId => {
  let response = await axios.get(`${config.API_MEMORY}/comments`, {
    params: {
      memoryId
    }
  });
  if (
    response &&
    response.data &&
    response.data.httpStatus === 200
  ) {
    return response.data.comments;
  }
  return null;
};

export const clearLocalMemories = tributId => {
  try {
    if (tributId) {
      localStorage.removeItem(`${CACHE_PREFIX}${tributId}`);
    } else {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (e) {
    console.warn('Error clearing localStorage', e);
  }
};

export const createMemory = async (tributId, message, file) => {
  const data = {
    tributId: tributId,
    message: message,
    mediaType: file.type,
    extension: file.fileExtension,
    fileName: file.fileName
  };

  let memory = await axios.put(config.API_MEMORY, data, {
    headers: getAuthHeader()
  });
  if (memory && memory.data && memory.data.httpStatus === 200) {
    clearLocalMemories(tributId);
    return memory.data.memoryId;
  }
  return null;
};

export const getMemoryCredentialsUploadImageS3 = async (
  tributId,
  memoryId,
  contentType,
  fileName,
  mediaType
) => {
  let response = await axios.get(
    config.API_MEMORY +
      '/key?tributId=' +
      tributId +
      '&memoryId=' +
      memoryId +
      '&contentType=' +
      contentType +
      '&fileName=' +
      fileName +
      '&mediaType=' +
      mediaType,
    { headers: getAuthHeader() }
  );
  if (
    response &&
    response.data &&
    response.data.httpStatus === 200
  ) {
    return response.data.credentials;
  }
  return null;
};

export const deleteMemory = async (memoryId, tributId) => {
  let deleteMemoryData = await axios.post(
    `${config.API_MEMORY}/delete`,
    {
      memoryId
    },
    {
      headers: getAuthHeader()
    }
  );
  if (deleteMemoryData.data.memory === true) {
    clearLocalMemories(tributId);
    return true;
  }
  return false;
};
